import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';
import { getUserFriendlyError } from '../utils/errorHandlers';

/**
 * Appointment Management Service
 *
 * Handles all operations related to appointments.
 */

/**
 * List appointments with filtering capabilities.
 *
 * @param {Object} filters - Filtering criteria.
 * @param {string} filters.tenantId - The tenant ID to scope the search.
 * @param {string} [filters.patientId] - Filter by patient ID.
 * @param {string} [filters.physicianId] - Filter by physician ID.
 * @param {string} [filters.status] - Filter by appointment status.
 * @returns {Promise<{data: Array, error: null}|{data: null, error: Error}>}
 */
export const listAppointments = async (filters) => {
  const { tenantId, patientId, physicianId, status } = filters;

  if (!tenantId) {
    const error = new Error('tenantId is required to list appointments.');
    toast.error(error.message);
    return { data: null, error };
  }

  try {
    let query = supabase
      .from('appointments')
      .select(`
        *,
        patient:users!appointments_patient_id_fkey ( first_name, last_name ),
        physician:users!appointments_physician_id_fkey ( first_name, last_name )
      `)
      .eq('tenant_id', tenantId);

    if (patientId) {
      query = query.eq('patient_id', patientId);
    }
    if (physicianId) {
      query = query.eq('physician_id', physicianId);
    }
    if (status) {
      query = query.eq('status', status);
    }

    query = query.order('scheduled_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error listing appointments:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

export default {
  listAppointments,
};
